import { lazy, Suspense, useEffect } from 'react';
import { Navbar } from './components/sections/Navbar';
import { Hero } from './components/sections/Hero';
import { LogosMarquee } from './components/sections/LogosMarquee';
import { LeadModalProvider } from './components/LeadModalContext';
import { CalculatorProvider } from './components/CalculatorContext';
import { LanguageProvider } from './i18n/LanguageContext';
import { initGA, track } from './lib/tracking';
import { captureUtm } from './lib/utm';
import { useScrollDepth } from './hooks/useScrollDepth';

// Lazy load das secoes abaixo do fold pra reduzir JS inicial
const RealityNow = lazy(() =>
  import('./components/sections/RealityNow').then((m) => ({ default: m.RealityNow }))
);
const Gap = lazy(() =>
  import('./components/sections/Gap').then((m) => ({ default: m.Gap }))
);
const MoneyOnTable = lazy(() =>
  import('./components/sections/MoneyOnTable').then((m) => ({
    default: m.MoneyOnTable,
  }))
);
const MethodPrisma = lazy(() =>
  import('./components/sections/MethodPrisma').then((m) => ({
    default: m.MethodPrisma,
  }))
);
const RevenueAIProduct = lazy(() =>
  import('./components/sections/RevenueAIProduct').then((m) => ({
    default: m.RevenueAIProduct,
  }))
);
const ArchitectureFunnel = lazy(() =>
  import('./components/sections/ArchitectureFunnel').then((m) => ({
    default: m.ArchitectureFunnel,
  }))
);
const WhyThux = lazy(() =>
  import('./components/sections/WhyThux').then((m) => ({
    default: m.WhyThux,
  }))
);
const BigNumbers = lazy(() =>
  import('./components/sections/BigNumbers').then((m) => ({ default: m.BigNumbers }))
);
const Cases = lazy(() =>
  import('./components/sections/Cases').then((m) => ({ default: m.Cases }))
);
const Testimonials = lazy(() =>
  import('./components/sections/Testimonials').then((m) => ({
    default: m.Testimonials,
  }))
);
const Outcome = lazy(() =>
  import('./components/sections/Outcome').then((m) => ({ default: m.Outcome }))
);
const HowItStarts = lazy(() =>
  import('./components/sections/HowItStarts').then((m) => ({
    default: m.HowItStarts,
  }))
);
const FAQ = lazy(() =>
  import('./components/sections/FAQ').then((m) => ({ default: m.FAQ }))
);
const Decision = lazy(() =>
  import('./components/sections/Decision').then((m) => ({ default: m.Decision }))
);
const Footer = lazy(() =>
  import('./components/sections/Footer').then((m) => ({ default: m.Footer }))
);
const LeadModal = lazy(() =>
  import('./components/LeadModal').then((m) => ({ default: m.LeadModal }))
);
const StickyBottomCTA = lazy(() =>
  import('./components/StickyBottomCTA').then((m) => ({
    default: m.StickyBottomCTA,
  }))
);

function PageView() {
  useScrollDepth();
  useEffect(() => {
    const utm = captureUtm();
    initGA();
    track('lp_revenue_ai_view', utm);
  }, []);
  return null;
}

function SectionFallback({ minHeight = 320 }: { minHeight?: number }) {
  return (
    <div
      aria-hidden
      className="w-full"
      style={{ minHeight: `${minHeight}px` }}
    />
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <LeadModalProvider>
        <CalculatorProvider>
          <PageView />
          <a
            href="#main"
            className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:rounded-pill focus:bg-cta-primary focus:text-white focus:text-sm focus:font-semibold focus:shadow-cta"
          >
            Pular para o conteúdo
          </a>
          <Navbar />
          <main id="main">
            {/* Above the fold: render imediato */}
            <Hero />
            <LogosMarquee />

            {/* Below the fold: lazy + Suspense */}
            <Suspense fallback={<SectionFallback minHeight={600} />}>
              <RealityNow />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={400} />}>
              <Gap />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={900} />}>
              <MoneyOnTable />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={500} />}>
              <MethodPrisma />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={1100} />}>
              <RevenueAIProduct />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={600} />}>
              <ArchitectureFunnel />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={600} />}>
              <WhyThux />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={300} />}>
              <BigNumbers />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={600} />}>
              <Cases />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={700} />}>
              <Testimonials />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={500} />}>
              <Outcome />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={700} />}>
              <HowItStarts />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={500} />}>
              <FAQ />
            </Suspense>
            <Suspense fallback={<SectionFallback minHeight={400} />}>
              <Decision />
            </Suspense>
          </main>
          <Suspense fallback={null}>
            <Footer />
          </Suspense>
          <Suspense fallback={null}>
            <LeadModal />
          </Suspense>
          <Suspense fallback={null}>
            <StickyBottomCTA />
          </Suspense>
        </CalculatorProvider>
      </LeadModalProvider>
    </LanguageProvider>
  );
}
